import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
  Snackbar,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  FilterList,
  Search,
  ReceiptLong,
  PendingActions,
  AccountBalanceWallet,
  Engineering,
  Assignment,
  OpenInNew,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Types — the WO -> SES -> Invoice -> Payment chain for a single service bill.
// ---------------------------------------------------------------------------

type ApprovalStatus = 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';
type PaymentStatus = 'UNPAID' | 'PARTIALLY_PAID' | 'PAID';
type MatchStatus = 'UNMATCHED' | 'MATCHED' | 'WITHIN_TOLERANCE' | 'EXCEPTION' | 'OVERRIDDEN';

interface BillVendor {
  id: number;
  company_name: string;
  vendor_code?: string;
  gstin?: string;
  pan?: string;
  tds_category?: string;
}

interface BillSwo {
  id: number;
  swo_number: string;
  status: string;
  total_value: number | string;
  wo_date: string;
}

interface BillSes {
  id: number;
  ses_number: string;
  status: string;
  total_amount: number | string;
  service_period_from?: string;
  service_period_to?: string;
}

interface ServiceBillRow {
  invoiceId: number;
  invoice_number: string;
  vendor_invoice_number?: string | null;
  invoice_date: string;
  due_date?: string | null;
  po_number?: string | null;
  vendor: BillVendor;
  swo: BillSwo | null;
  ses: BillSes | null;
  gross_amount: number | string;
  gst: number | string;
  tds: number | string;
  net_payable: number | string;
  paid_amount: number | string;
  balance_due: number | string;
  match_status: MatchStatus | string;
  approval_status: ApprovalStatus;
  payment_status: PaymentStatus;
}

interface BillSummary {
  totalCount: number;
  totalGross: number | string;
  totalGst: number | string;
  totalTds: number | string;
  totalNetPayable: number | string;
  totalBalanceDue: number | string;
  pendingApprovalCount: number;
  pendingApprovalValue: number | string;
  outstandingCount: number;
  outstandingValue: number | string;
}

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const APPROVAL_TONE: Record<ApprovalStatus, StatusTone> = {
  PENDING_APPROVAL: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
};

const PAYMENT_TONE: Record<PaymentStatus, StatusTone> = {
  UNPAID: 'error',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
};

const MATCH_TONE: Record<string, StatusTone> = {
  UNMATCHED: 'neutral',
  MATCHED: 'success',
  WITHIN_TOLERANCE: 'warning',
  EXCEPTION: 'error',
  OVERRIDDEN: 'info',
};

const ServiceBillPassingPage: React.FC = () => {
  const navigate = useNavigate();

  const [bills, setBills] = useState<ServiceBillRow[]>([]);
  const [summary, setSummary] = useState<BillSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatus | ''>('');
  const [approvalStatusFilter, setApprovalStatusFilter] = useState<ApprovalStatus | ''>('');
  const [search, setSearch] = useState('');

  const [passingId, setPassingId] = useState<number | null>(null);

  const fetchSummary = useCallback(() => {
    api
      .get<{ summary: BillSummary }>('/service-bill-passing/summary')
      .then((r) => setSummary(r.summary))
      .catch(() => {
        // Stat row stays hidden — never fabricate these numbers.
      });
  }, []);

  const fetchBills = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ bills: ServiceBillRow[]; count: number }>('/service-bill-passing', {
        params: {
          paymentStatus: paymentStatusFilter || undefined,
          approvalStatus: approvalStatusFilter || undefined,
        },
      })
      .then((r) => setBills(r.bills || []))
      .catch((err: any) => setError(err.message || 'Failed to load service bills'))
      .finally(() => setLoading(false));
  }, [paymentStatusFilter, approvalStatusFilter]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const refreshAll = () => {
    fetchSummary();
    fetchBills();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bills;
    return bills.filter(
      (b) =>
        b.invoice_number?.toLowerCase().includes(q) ||
        b.vendor?.company_name?.toLowerCase().includes(q) ||
        b.swo?.swo_number?.toLowerCase().includes(q) ||
        b.ses?.ses_number?.toLowerCase().includes(q)
    );
  }, [bills, search]);

  const hasFilters = !!(paymentStatusFilter || approvalStatusFilter || search);
  const clearFilters = () => {
    setPaymentStatusFilter('');
    setApprovalStatusFilter('');
    setSearch('');
  };

  const handlePass = async (invoiceId: number) => {
    setPassingId(invoiceId);
    try {
      await api.post(`/service-bill-passing/${invoiceId}/pass`);
      setToast({ message: 'Service bill passed for payment.', severity: 'success' });
      refreshAll();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to pass service bill', severity: 'error' });
    } finally {
      setPassingId(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Service Bill Passing"
        subtitle="Work order → service entry → invoice → payment, reconciled before a service bill can be paid"
        actions={
          <>
            <IconButton onClick={refreshAll} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button size="small" onClick={() => navigate('/procurement/invoices')}>
              All Invoices
            </Button>
            <Button size="small" onClick={() => navigate('/procurement/payments')}>
              All Payments
            </Button>
          </>
        }
      />

      {summary && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Bills Pending Approval"
              value={summary.pendingApprovalCount}
              icon={<PendingActions />}
              color="#d97706"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Pending Approval Value"
              value={formatIndianNumber(toNum(summary.pendingApprovalValue))}
              icon={<PendingActions />}
              color="#d97706"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Outstanding Bills"
              value={summary.outstandingCount}
              icon={<AccountBalanceWallet />}
              color="#dc2626"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Unpaid Value"
              value={formatIndianNumber(toNum(summary.outstandingValue))}
              icon={<AccountBalanceWallet />}
              color="#dc2626"
            />
          </Grid>
        </Grid>
      )}

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search invoice, vendor, SWO or SES"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Approval Status"
          value={approvalStatusFilter}
          onChange={(e) => setApprovalStatusFilter(e.target.value as ApprovalStatus | '')}
          sx={{ minWidth: 180 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Approval Statuses</MenuItem>
          <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
          <MenuItem value="APPROVED">Approved</MenuItem>
          <MenuItem value="REJECTED">Rejected</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Payment Status"
          value={paymentStatusFilter}
          onChange={(e) => setPaymentStatusFilter(e.target.value as PaymentStatus | '')}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All Payment Statuses</MenuItem>
          <MenuItem value="UNPAID">Unpaid</MenuItem>
          <MenuItem value="PARTIALLY_PAID">Partially Paid</MenuItem>
          <MenuItem value="PAID">Paid</MenuItem>
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        {error ? (
          <ErrorState message={error} onRetry={fetchBills} />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Invoice</TableCell>
                  <TableCell sx={headerCellSx}>Vendor</TableCell>
                  <TableCell sx={headerCellSx}>Work Order</TableCell>
                  <TableCell sx={headerCellSx}>Service Entry</TableCell>
                  <TableCell sx={headerCellSx} align="right">Gross</TableCell>
                  <TableCell sx={headerCellSx} align="right">GST</TableCell>
                  <TableCell sx={headerCellSx} align="right">TDS</TableCell>
                  <TableCell sx={headerCellSx} align="right">Net Payable</TableCell>
                  <TableCell sx={headerCellSx} align="right">Balance Due</TableCell>
                  <TableCell sx={headerCellSx}>Match</TableCell>
                  <TableCell sx={headerCellSx}>Approval</TableCell>
                  <TableCell sx={headerCellSx}>Payment</TableCell>
                  <TableCell sx={headerCellSx} align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} sx={{ border: 0 }}>
                      <TableEmptyState
                        title="No service bills match these filters"
                        hint="Clear the filters, or check back once a service invoice has been recorded."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((b) => (
                    <TableRow key={b.invoiceId} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.25 }}>
                        <CodeCell code={b.invoice_number} tint="#0284c7" icon={<ReceiptLong fontSize="small" />} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {fmtDate(b.invoice_date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {b.vendor?.company_name || '—'}
                      </TableCell>
                      <TableCell>
                        {b.swo ? (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            onClick={() => navigate(`/procurement/service-work-orders/${b.swo!.id}`)}
                          >
                            <Engineering sx={{ fontSize: 15, color: '#0284c7' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0284c7' }}>
                              {b.swo.swo_number}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {b.ses ? (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            onClick={() => navigate(`/procurement/service-entries/${b.ses!.id}`)}
                          >
                            <Assignment sx={{ fontSize: 15, color: '#7c3aed' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#7c3aed' }}>
                              {b.ses.ses_number}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(toNum(b.gross_amount))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(toNum(b.gst))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(toNum(b.tds))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatIndianNumber(toNum(b.net_payable))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatIndianNumber(toNum(b.balance_due))}
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          label={String(b.match_status).replace('_', ' ')}
                          tone={MATCH_TONE[b.match_status] || 'neutral'}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusDot label={b.approval_status.replace('_', ' ')} tone={APPROVAL_TONE[b.approval_status] || 'neutral'} />
                      </TableCell>
                      <TableCell>
                        <StatusDot label={b.payment_status.replace('_', ' ')} tone={PAYMENT_TONE[b.payment_status] || 'neutral'} />
                      </TableCell>
                      <TableCell align="right">
                        {b.approval_status === 'PENDING_APPROVAL' ? (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={passingId === b.invoiceId}
                            onClick={() => handlePass(b.invoiceId)}
                          >
                            {passingId === b.invoiceId ? 'Passing…' : 'Pass'}
                          </Button>
                        ) : (
                          <Tooltip title="View in invoices">
                            <IconButton size="small" onClick={() => navigate('/procurement/invoices')}>
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TableSurface>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default ServiceBillPassingPage;
